import {action} from "mobx";
import React from "react";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {MapTool, Tool, ToolData, ToolInterceptSubresult} from "./@Tool.js";
import {store} from "../../../../../Store/index.js";
import {MapDragInfo} from "./Helpers/DragHelper.js";
import {Vec2, Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {O, RunInAction, RunInAction_Set} from "web-vcore";
import {MaybeSnapPosChange, MaybeSnapWorldPos, RotateOffsetByRotation, TransformObjLocalPointToWorldOffset_UsingRotationOnly, TransformWorldOffsetToObjLocalPoint_UsingRotationOnly} from "../../../../../Utils/ThreeJS/SpaceUtils.js";
import {GetOpenMapID} from "../../../../../Store/main/maps.js";
import {GetActiveObject, GetMapState, GetSelectedObjects} from "../../../../../Store/main/maps/$mapState.js";
import {GetMapObjects} from "../../../../../Server/DB/mapObjects.js";
import {UpdateMapObject} from "../../../../../Server/Commands/MapObjectCommands.js";
import {ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../../../../Server/DB/mapObjects/$mapObject.js";
import {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {Select, Text} from "react-vcomponents";
import {MapControls} from "../../MapSurface/MapControls.js";
import {observer_mgl} from "mobx-graphlink";
import {ignore} from "mobx-sync";

type EdgeOrCorner = "corner_topLeft" | "corner_topRight" | "corner_bottomLeft" | "corner_bottomRight" | "edge_top" | "edge_bottom" | "edge_left" | "edge_right";

export enum ResizeSpace {
	local = "local",
	global = "global",
}

export class ToolState_Resize {
	@O accessor space = ResizeSpace.global;
	@O @ignore accessor currentDrag: MapDragInfo<{obj: MapObject, dragType: EdgeOrCorner, canvas: HTMLCanvasElement, controls: MapControls}>|n;
	@action EndCurrentDrag() {
		if (this.currentDrag == null) return;
		this.currentDrag.extras.canvas.style.cursor = "";
		this.currentDrag = null;
	}
}

export function Get3JSCanvas() {
	return document.querySelector("#threejs-container")?.querySelector("canvas") as HTMLCanvasElement|n;
}

export class Tool_Resize extends Tool {
	toolType = MapTool.resize;
	get ToolState() { return store.main.maps.tools.toolStates.resize; }

	RenderInToolbox = observer_mgl(({data}: {data: ToolData.RenderInToolbox})=>{
		const state = this.ToolState;
		const drag = state.currentDrag;
		const newData = drag ? this.CurrentDrag_GetNewPosAndSize(drag.currentPos_world) : null;
		const {newSize} = newData ?? {};
		return <>
			<Select ml={5} displayType="button bar" options={[
				{name: "Local", value: ResizeSpace.local},
				{name: "Global", value: ResizeSpace.global},
			]} value={state.space} onChange={val=>RunInAction_Set(()=>state.space = val)}/>
			{drag && newSize && <Text ml={5}>{`Size: ${newSize.x.toFixed(1)}m x ${newSize.y.toFixed(1)}m`}</Text>}
		</>;
	});
	RenderInScene = observer_mgl_r3(({data}: {data: ToolData.RenderInScene})=>{
		const {map, mapState} = data;
		return <>
		</>;
	});
	RenderInSceneOverlay = observer_mgl((props: {data: ToolData.RenderInSceneOverlay})=>{
		const state = this.ToolState;
		const drag = state.currentDrag;
		if (drag == null) return null;
		const {controls} = drag.extras;
		const controls_DOM = controls?.DOM;
		if (!controls_DOM) return null;

		const newData = this.CurrentDrag_GetNewPosAndSize(drag.currentPos_world);
		if (!newData) return null;

		const {newPos_world, newSize} = newData;
		const halfSize = newSize.Times(0.5);
		// Compute axis-aligned screen size (ignoring rotation) then apply CSS rotation
		const topLeft_screen = controls_DOM.WorldPosToClientPos(newPos_world.ToVec2().Plus(new Vec2(-halfSize.x, halfSize.y)));
		const bottomRight_screen = controls_DOM.WorldPosToClientPos(newPos_world.ToVec2().Plus(new Vec2(halfSize.x, -halfSize.y)));
		const center_screen = controls_DOM.WorldPosToClientPos(newPos_world.ToVec2());
		const width = bottomRight_screen.x - topLeft_screen.x;
		const height = bottomRight_screen.y - topLeft_screen.y;
		const objRot_world = ResolveObjRotInWorld.CatchBail(null, drag.extras.obj) ?? 0;
		// objRot_world is in degrees, CW — same convention as CSS rotate()
		const rotateDeg = objRot_world;

		return <div style={{
			position: "fixed",
			border: "2px solid orange",
			pointerEvents: "none",
			zIndex: 100,
			left: `${center_screen.x - width / 2}px`,
			top: `${center_screen.y - height / 2}px`,
			width: `${width}px`,
			height: `${height}px`,
			transform: `rotate(${rotateDeg}deg)`,
			transformOrigin: "center center",
		}}/>;
	});

	Tool_OnDeactivate(data: ToolData.Tool_OnDeactivate): void {
		this.ToolState.EndCurrentDrag();
	}
	Document_OnKeyDown(data: ToolData.Document_OnKeyDown): ToolInterceptSubresult | void {
		if (data.event.key == "Escape") {
			this.ToolState.EndCurrentDrag();
			return {absorb: true};
		}
	}

	Scene_OnPointerDown(data: ToolData.Scene_OnPointerClick) {
		const {event, mousePos, mousePos_world, controls} = data;
		if (event.button != 0) return;

		const state = this.ToolState;
		if (state.currentDrag == null) {
			const mapID = GetOpenMapID.CatchBail(null);
			const activeObj = mapID ? GetActiveObject.CatchBail(null, mapID) : null;
			const dragType = activeObj && activeObj.shape_size ? this.GetEdgeOrCornerForDragging(activeObj, mousePos_world, controls) : undefined;
			if (dragType) {
				RunInAction("Resize.Scene_OnPointerDown", ()=>{
					state.currentDrag = new MapDragInfo({
						button: event.button,
						startPos_mouse: mousePos,
						startPos_world: mousePos_world,
						currentPos_mouse: mousePos,
						currentPos_world: mousePos_world,
						extras: {obj: activeObj, dragType: dragType, canvas: Get3JSCanvas(), controls},
					});
				});
			}
		}
	}

	Document_OnPointerMove(data: ToolData.Document_OnPointerMove): void | ToolInterceptSubresult {
		const {event, mousePos, mousePos_world, controls} = data;
		const state = this.ToolState;
		const mapID = GetOpenMapID.CatchBail(null);
		if (!mapID) return;
		const canvas = Get3JSCanvas();

		const drag = state.currentDrag;
		if (drag) {
			RunInAction("Resize.Document_OnPointerMove", ()=>{
				drag.currentPos_mouse = mousePos;
				drag.currentPos_world = mousePos_world;
			});
			if (canvas) {
			const objRot = ResolveObjRotInWorld.CatchBail(null, drag.extras.obj) ?? 0;
			canvas.style.cursor = drag.extras.dragType ? this.GetCursorForEdgeOrCorner(drag.extras.dragType, objRot) : "default";
		}
		} else {
			// adjust cursor based on if user is near an edge/corner (or if drag is active, with a drag started with a given cursor)
			if (canvas) {
				let newCursor = "default";
				const activeObj = GetActiveObject.CatchBail(null, mapID);
				if (activeObj && activeObj.shape_size) {
					const edgeOrCorner = this.GetEdgeOrCornerForDragging(activeObj, mousePos_world, controls);
					if (edgeOrCorner) {
						const objRot = ResolveObjRotInWorld.CatchBail(null, activeObj) ?? 0;
						newCursor = this.GetCursorForEdgeOrCorner(edgeOrCorner, objRot);
					}
				}
				canvas.style.cursor = newCursor;
			}
		}
	}

	/** Note: The returned newPos is the object's local position. */
	CurrentDrag_GetNewPosAndSize(mousePos_world: Vec3) {
		const state = this.ToolState;
		const drag = state.currentDrag;
		if (!drag) return null;
		const {obj, dragType, controls} = drag.extras;
		const controls_DOM = controls?.DOM;
		if (!controls_DOM) return null;
		const mapID = GetOpenMapID.CatchBail(null);
		if (!mapID) return null;
		const mapState = GetMapState.CatchBail(null, mapID);
		if (mapState == null) return null;
		const mapObjects = GetMapObjects.CatchBail(null, mapID);
		if (!mapObjects) return null;

		if (obj.shape_size == null) return null;
		// create shorter aliases (while also fixing types)
		let newSize = obj.shape_size.Cast(Vec2);
		let newPos_local = obj.position.Cast(Vec3);

		// In global mode, snap the world-space mouse-delta up front. In local mode, snapping is applied to the
		// resulting local position & size at the end, so keep the raw delta here.
		const space = state.space;
		const startPos_world = drag.startPos_world.ToVec2();
		const posDiff = mousePos_world.ToVec2().Minus(startPos_world);
		const edgeOrCornerShift = space == ResizeSpace.global ? MaybeSnapPosChange(posDiff) : posDiff;

		// Decompose world-space shift into the object's local axes
		const objRot = ResolveObjRotInWorld.CatchBail(null, obj) ?? 0;
		let shiftLocal = TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(edgeOrCornerShift, objRot);
		// local mode: snap the dragged edge(s) to the grid along obj's axes (in parent-local space), so the opposite edge stays anchored
		if (space == ResizeSpace.local) {
			const dragType_lower = dragType.toLowerCase();
			const edgeSign = new Vec2(dragType_lower.includes("left") ? -1 : 1, dragType_lower.includes("bottom") ? -1 : 1);
			const objPos_alongObjAxes = TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(new Vec2(obj.position.x, obj.position.y), obj.rotation ?? 0);
			const edgePos_old = objPos_alongObjAxes.Plus(newSize.Times(.5).Times(edgeSign));
			const edgePos_new_snapped = MaybeSnapWorldPos(edgePos_old.Plus(shiftLocal));
			shiftLocal = edgePos_new_snapped.Minus(edgePos_old);
		}
		const {x: shiftLocal_x, y: shiftLocal_y} = shiftLocal;
		// Helper: convert an object-local center shift into a parent-local offset (what newPos_local is expressed in).
		// Uses only the object's OWN rotation — the parent's rotation is already baked into the coordinate system of newPos_local.
		const localToParentLocalShift = (lx: number, ly: number) => RotateOffsetByRotation(new Vec2(lx, ly), obj.rotation ?? 0).ToVec3(0);

		if (dragType == "corner_topLeft") {
			newSize = newSize.Plus(new Vec2(-shiftLocal_x, shiftLocal_y));
			newPos_local = newPos_local.Plus(localToParentLocalShift(shiftLocal_x / 2, shiftLocal_y / 2));
		} else if (dragType == "corner_topRight") {
			newSize = newSize.Plus(new Vec2(shiftLocal_x, shiftLocal_y));
			newPos_local = newPos_local.Plus(localToParentLocalShift(shiftLocal_x / 2, shiftLocal_y / 2));
		} else if (dragType == "corner_bottomLeft") {
			newSize = newSize.Plus(new Vec2(-shiftLocal_x, -shiftLocal_y));
			newPos_local = newPos_local.Plus(localToParentLocalShift(shiftLocal_x / 2, shiftLocal_y / 2));
		} else if (dragType == "corner_bottomRight") {
			newSize = newSize.Plus(new Vec2(shiftLocal_x, -shiftLocal_y));
			newPos_local = newPos_local.Plus(localToParentLocalShift(shiftLocal_x / 2, shiftLocal_y / 2));
		} else if (dragType == "edge_top") {
			newSize = newSize.Plus(new Vec2(0, shiftLocal_y));
			newPos_local = newPos_local.Plus(localToParentLocalShift(0, shiftLocal_y / 2));
		} else if (dragType == "edge_bottom") {
			newSize = newSize.Plus(new Vec2(0, -shiftLocal_y));
			newPos_local = newPos_local.Plus(localToParentLocalShift(0, shiftLocal_y / 2));
		} else if (dragType == "edge_left") {
			newSize = newSize.Plus(new Vec2(-shiftLocal_x, 0));
			newPos_local = newPos_local.Plus(localToParentLocalShift(shiftLocal_x / 2, 0));
		} else if (dragType == "edge_right") {
			newSize = newSize.Plus(new Vec2(shiftLocal_x, 0));
			newPos_local = newPos_local.Plus(localToParentLocalShift(shiftLocal_x / 2, 0));
		}

		const oldPos_world = ResolveObjPosInWorld.CatchBail(null, obj);
		if (!oldPos_world) return null;
		// Convert the parent-local position change into a world-space offset using the parent's world rotation,
		// then apply it to the original world position.
		const parentRot = objRot - (obj.rotation ?? 0);
		const posChange_parentLocal = newPos_local.ToVec2().Minus(obj.position);
		const posChange_world = TransformObjLocalPointToWorldOffset_UsingRotationOnly(posChange_parentLocal, parentRot);
		const newPos_world = oldPos_world.Plus(posChange_world.ToVec3(0));

		return {newPos_local, newPos_world, newSize};
	}

	Document_OnPointerUp(data: ToolData.Document_OnPointerUp) {
		const {event, mousePos_world, controls} = data;
		const state = this.ToolState;
		const drag = state.currentDrag;
		if (drag && drag?.button == event.button) {
			const mapID = GetOpenMapID.CatchBail(null);
			if (!mapID) return;

			// effect
			(async()=>{
				const {newSize, newPos_local} = this.CurrentDrag_GetNewPosAndSize(mousePos_world) ?? {};
				if (!newSize || !newPos_local) return;

				// Ensure size doesn't go negative
				if (newSize.x < 0.1 || newSize.y < 0.1) return;

				await new UpdateMapObject({
					id: drag.extras.obj.id,
					updates: {
						shape_size: newSize,
						position: newPos_local,
					},
				}).RunOnServer();
			})();

			this.ToolState.EndCurrentDrag();
		}
	}

	GetEdgeOrCornerForDragging(obj: MapObject, mousePos_world: Vec3, controls: MapControls): EdgeOrCorner|null {
		const controls_DOM = controls?.DOM;
		const closests = this.GetClosestCornerAndEdge(obj, mousePos_world.ToVec2());
		if (controls_DOM && closests) {
			const maxDist = 10 * controls_DOM.OnePixelAsMeters().x; // 10 pixels, converted to meters
			if (closests.corner.distance <= maxDist) return closests.corner.point;
			if (closests.edge.distance <= maxDist) return closests.edge.point;
		}
		return null;
	}
	GetClosestCornerAndEdge(obj: MapObject, mousePos_world: Vec2): {corner: {point: EdgeOrCorner, distance: number}, edge: {point: EdgeOrCorner, distance: number}}|null {
		const objPos_world = ResolveObjPosInWorld(obj);
		if (!objPos_world || !obj.shape_size) return null;

		const objCenter = objPos_world.ToVec2();
		const halfSize = obj.shape_size.Cast(Vec2).Times(0.5);

		// Rotate local-space offsets into world space using the object's world rotation
		const objRot = ResolveObjRotInWorld(obj) ?? 0;
		const rotOfs = (lx: number, ly: number) => TransformObjLocalPointToWorldOffset_UsingRotationOnly(new Vec2(lx, ly), objRot);

		// Calculate the 4 corners in world space
		const corners = {
			corner_topLeft: objCenter.Plus(rotOfs(-halfSize.x, halfSize.y)),
			corner_topRight: objCenter.Plus(rotOfs(halfSize.x, halfSize.y)),
			corner_bottomLeft: objCenter.Plus(rotOfs(-halfSize.x, -halfSize.y)),
			corner_bottomRight: objCenter.Plus(rotOfs(halfSize.x, -halfSize.y)),
		};

		// Find the closest corner
		let closestCornerDist = Infinity;
		let closestCorner: EdgeOrCorner = "corner_topLeft";

		for (const [key, point] of Object.entries(corners)) {
			const dist = mousePos_world.DistanceTo(point);
			if (dist < closestCornerDist) {
				closestCornerDist = dist;
				closestCorner = key as EdgeOrCorner;
			}
		}

		// Calculate distance to each edge (distance to closest point on the edge line segment)
		const edges = {
			edge_top: {start: corners.corner_topLeft, end: corners.corner_topRight},
			edge_bottom: {start: corners.corner_bottomLeft, end: corners.corner_bottomRight},
			edge_left: {start: corners.corner_bottomLeft, end: corners.corner_topLeft},
			edge_right: {start: corners.corner_bottomRight, end: corners.corner_topRight},
		};

		let closestEdgeDist = Infinity;
		let closestEdge: EdgeOrCorner = "edge_top";

		for (const [key, {start, end}] of Object.entries(edges)) {
			// Calculate distance from point to line segment
			const lineVec = end.Minus(start);
			const pointVec = mousePos_world.Minus(start);
			const lineLen = lineVec.DistanceTo(Vec2.zero);
			if (lineLen === 0) continue;

			// Project point onto line and clamp to segment
			const dotProduct = (pointVec.x * lineVec.x) + (pointVec.y * lineVec.y);
			const t = Math.max(0, Math.min(1, dotProduct / (lineLen * lineLen)));
			const projection = start.Plus(lineVec.Times(t));
			const dist = mousePos_world.DistanceTo(projection);

			if (dist < closestEdgeDist) {
				closestEdgeDist = dist;
				closestEdge = key as EdgeOrCorner;
			}
		}

		return {
			corner: {point: closestCorner, distance: closestCornerDist},
			edge: {point: closestEdge, distance: closestEdgeDist}
		};
	}

	/**
	 * Returns the CSS resize cursor that matches the actual screen direction of the drag handle,
	 * accounting for the object's world rotation.
	 */
	GetCursorForEdgeOrCorner(edgeOrCorner: EdgeOrCorner, objRot: number = 0): string {
		// Nominal drag direction in object-local space (points away from center, perpendicular to edge)
		let localDirX: number, localDirY: number;
		switch (edgeOrCorner) {
			case "edge_top":          localDirX =  0; localDirY =  1; break;
			case "edge_bottom":       localDirX =  0; localDirY = -1; break;
			case "edge_left":         localDirX = -1; localDirY =  0; break;
			case "edge_right":        localDirX =  1; localDirY =  0; break;
			case "corner_topLeft":    localDirX = -1; localDirY =  1; break;
			case "corner_topRight":   localDirX =  1; localDirY =  1; break;
			case "corner_bottomLeft": localDirX = -1; localDirY = -1; break;
			case "corner_bottomRight": localDirX = 1; localDirY = -1; break;
			default: return "default";
		}
		// Rotate into world space, then flip Y (world Y-up → screen Y-down) to get screen direction
		const worldOffset = TransformObjLocalPointToWorldOffset_UsingRotationOnly(new Vec2(localDirX, localDirY), objRot);
		const angleDeg = Math.atan2(-worldOffset.y, worldOffset.x) * 180 / Math.PI;
		// Snap to the nearest of the 4 bidirectional CSS resize cursors (45° bands)
		const a = ((angleDeg % 180) + 180) % 180;
		if (a < 22.5 || a >= 157.5) return "ew-resize";
		if (a < 67.5) return "nwse-resize";
		if (a < 112.5) return "ns-resize";
		return "nesw-resize";
	}
}